import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { adminApi } from '../api/adminApi';

interface PhysicalPersonData {
  firstName: string;
  lastName?: string;
  email: string;
  phone?: string;
  password: string;
  confirmPassword: string;
  newsletterSubscription: boolean;
}

interface LegalEntityData {
  entityType: string;
  companyName: string;
  inn: string;
  kpp?: string;
  companyPhone?: string;
  legalAddress: string;
  contactPersonName: string;
  contactPersonPhone: string;
  contactPersonEmail: string;
  password: string;
  confirmPassword: string;
}

type RegistrationType = 'physical' | 'legal';

export default function AdminUserRegistrationPage() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [activeTab, setActiveTab] = useState<RegistrationType>('physical');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Данные для физического лица
  const [physicalData, setPhysicalData] = useState<PhysicalPersonData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    newsletterSubscription: false
  });

  // Данные для юридического лица
  const [legalData, setLegalEntityData] = useState<LegalEntityData>({
    entityType: '',
    companyName: '',
    inn: '',
    kpp: '',
    companyPhone: '',
    legalAddress: '',
    contactPersonName: '',
    contactPersonPhone: '',
    contactPersonEmail: '',
    password: '',
    confirmPassword: ''
  });

  // Проверяем права администратора
  if (!hasRole('Admin')) {
    navigate('/');
    return null;
  }

  const handlePhysicalDataChange = (field: keyof PhysicalPersonData, value: string | boolean) => {
    setPhysicalData(prev => ({ ...prev, [field]: value }));
  };

  const handleLegalDataChange = (field: keyof LegalEntityData, value: string | boolean) => {
    setLegalEntityData(prev => ({ ...prev, [field]: value }));
  };

  const handlePhysicalPersonSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsSubmitting(true);

    if (physicalData.password !== physicalData.confirmPassword) {
      setError('Пароли не совпадают');
      setIsSubmitting(false);
      return;
    }

    if (physicalData.password.length < 4) {
      setError('Пароль должен содержать минимум 4 символа');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await adminApi.createPhysicalPerson(physicalData);
      if (response.success) {
        setSuccess('Пользователь успешно создан');
        setPhysicalData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          password: '',
          confirmPassword: '',
          newsletterSubscription: false
        });
      } else {
        setError(response.message || 'Ошибка при создании пользователя');
      }
    } catch (err) {
      setError('Ошибка при создании пользователя');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLegalEntitySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsSubmitting(true);

    if (legalData.password !== legalData.confirmPassword) {
      setError('Пароли не совпадают');
      setIsSubmitting(false);
      return;
    }

    if (legalData.password.length < 4) {
      setError('Пароль должен содержать минимум 4 символа');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await adminApi.createLegalEntity(legalData);
      if (response.success) {
        setSuccess('Юридическое лицо успешно создано');
        setLegalEntityData({
          entityType: '',
          companyName: '',
          inn: '',
          kpp: '',
          companyPhone: '',
          legalAddress: '',
          contactPersonName: '',
          contactPersonPhone: '',
          contactPersonEmail: '',
          password: '',
          confirmPassword: ''
        });
      } else {
        setError(response.message || 'Ошибка при создании юридического лица');
      }
    } catch (err) {
      setError('Ошибка при создании юридического лица');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-10">
      <h1 className="text-2xl font-bold mb-6">Регистрация пользователей</h1>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4">
          {success}
        </div>
      )}

      {/* Табы */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          onClick={() => setActiveTab('physical')}
          className={`px-6 py-3 text-sm font-medium rounded-t-lg ${
            activeTab === 'physical'
              ? 'bg-white border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Физические лица
        </button>
        <button
          onClick={() => setActiveTab('legal')}
          className={`px-6 py-3 text-sm font-medium rounded-t-lg ${
            activeTab === 'legal'
              ? 'bg-white border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Юридические лица
        </button>
      </div>

      {/* Форма физического лица */}
      {activeTab === 'physical' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-medium mb-6">Регистрация физического лица</h2>
          
          <form onSubmit={handlePhysicalPersonSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 pl-4">Имя*</label>
                <input
                  type="text"
                  required
                  value={physicalData.firstName}
                  onChange={(e) => handlePhysicalDataChange('firstName', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent placeholder-gray-400"
                  placeholder="Введите имя"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 pl-4">Фамилия</label>
                <input
                  type="text"
                  value={physicalData.lastName}
                  onChange={(e) => handlePhysicalDataChange('lastName', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent placeholder-gray-400"
                  placeholder="Введите фамилию"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 pl-4">Email*</label>
                <input
                  type="email"
                  required
                  value={physicalData.email}
                  onChange={(e) => handlePhysicalDataChange('email', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent placeholder-gray-400"
                  placeholder="Введите email"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 pl-4">Телефон</label>
                <input
                  type="tel"
                  value={physicalData.phone}
                  onChange={(e) => handlePhysicalDataChange('phone', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent placeholder-gray-400"
                  placeholder="Введите телефон"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 pl-4">Пароль*</label>
                <input
                  type="password"
                  required
                  value={physicalData.password}
                  onChange={(e) => handlePhysicalDataChange('password', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent placeholder-gray-400"
                  placeholder="Введите пароль"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 pl-4">Подтвердите пароль*</label>
                <input
                  type="password"
                  required
                  value={physicalData.confirmPassword}
                  onChange={(e) => handlePhysicalDataChange('confirmPassword', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent placeholder-gray-400"
                  placeholder="Подтвердите пароль"
                />
              </div>
            </div>

            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={physicalData.newsletterSubscription}
                  onChange={(e) => handlePhysicalDataChange('newsletterSubscription', e.target.checked)}
                  className="rounded-full border-gray-300 text-gray-600 focus:ring-gray-500"
                />
                <span className="text-sm text-gray-700">Подписка на рассылку</span>
              </label>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2 bg-black text-white rounded-full hover:bg-gray-800 disabled:bg-gray-400"
              >
                {isSubmitting ? 'Создание...' : 'Создать пользователя'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Форма юридического лица */}
      {activeTab === 'legal' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-medium mb-6">Регистрация юридического лица</h2>
          
          <form onSubmit={handleLegalEntitySubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 pl-4">Тип организации*</label>
                <select
                  required
                  value={legalData.entityType}
                  onChange={(e) => handleLegalDataChange('entityType', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                >
                  <option value="">Выберите тип</option>
                  <option value="ООО">ООО</option>
                  <option value="ИП">ИП</option>
                  <option value="АО">АО</option>
                  <option value="ЗАО">ЗАО</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 pl-4">Название организации*</label>
                <input
                  type="text"
                  required
                  value={legalData.companyName}
                  onChange={(e) => handleLegalDataChange('companyName', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent placeholder-gray-400"
                  placeholder="Введите название организации"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 pl-4">ИНН*</label>
                <input
                  type="text"
                  required
                  value={legalData.inn}
                  onChange={(e) => handleLegalDataChange('inn', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent placeholder-gray-400"
                  placeholder="Введите ИНН"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 pl-4">КПП</label>
                <input
                  type="text"
                  value={legalData.kpp}
                  onChange={(e) => handleLegalDataChange('kpp', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent placeholder-gray-400"
                  placeholder="Введите КПП"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 pl-4">Телефон организации</label>
                <input
                  type="tel"
                  value={legalData.companyPhone}
                  onChange={(e) => handleLegalDataChange('companyPhone', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent placeholder-gray-400"
                  placeholder="Введите телефон организации"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 pl-4">Юридический адрес*</label>
                <input
                  type="text"
                  required
                  value={legalData.legalAddress}
                  onChange={(e) => handleLegalDataChange('legalAddress', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent placeholder-gray-400"
                  placeholder="Введите юридический адрес"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 pl-4">Контактное лицо*</label>
                <input
                  type="text"
                  required
                  value={legalData.contactPersonName}
                  onChange={(e) => handleLegalDataChange('contactPersonName', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent placeholder-gray-400"
                  placeholder="ФИО контактного лица"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 pl-4">Телефон контактного лица*</label>
                <input
                  type="tel"
                  required
                  value={legalData.contactPersonPhone}
                  onChange={(e) => handleLegalDataChange('contactPersonPhone', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent placeholder-gray-400"
                  placeholder="Введите телефон контактного лица"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 pl-4">Email контактного лица*</label>
              <input
                type="email"
                required
                value={legalData.contactPersonEmail}
                onChange={(e) => handleLegalDataChange('contactPersonEmail', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent placeholder-gray-400"
                placeholder="Введите email контактного лица"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 pl-4">Пароль*</label>
                <input
                  type="password"
                  required
                  value={legalData.password}
                  onChange={(e) => handleLegalDataChange('password', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent placeholder-gray-400"
                  placeholder="Введите пароль"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 pl-4">Подтвердите пароль*</label>
                <input
                  type="password"
                  required
                  value={legalData.confirmPassword}
                  onChange={(e) => handleLegalDataChange('confirmPassword', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent placeholder-gray-400"
                  placeholder="Подтвердите пароль"
                />
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2 bg-black text-white rounded-full hover:bg-gray-800 disabled:bg-gray-400"
              >
                {isSubmitting ? 'Создание...' : 'Создать юридическое лицо'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
